import type { PlatformActionRequest } from '../models/platformAction.types'
import { adaptActionRequest } from '../service/adaptActionRequest'
import { classifyError } from '../service/classifyError'

import type { DecideActionApi, DetailActionStore, FlowResult } from './platformActionFlow.types'

export interface ConfirmActionFlowDeps {
  api: DecideActionApi
  store: DetailActionStore
}

/**
 * Step 2 of the handshake: spends the `confirm_token` and moves the request to `queued`.
 *
 * The load-bearing failure is 409 `CONFIRM_TOKEN_EXPIRED` / `CONFIRM_TOKEN_INVALID` — the preview
 * the user approved is no longer the one the backend would execute. `classifyError` marks those
 * with `requiresRepropose`, and the modal turns that into "this preview is stale, refresh it"
 * rather than a generic failure (ticket acceptance criterion 2).
 */
export class ConfirmActionFlow {
  constructor(private readonly deps: ConfirmActionFlowDeps) {}

  async execute(
    workspaceId: number,
    actionId: string,
    confirmToken: string,
    comment?: string,
    signal?: AbortSignal,
  ): Promise<FlowResult<PlatformActionRequest>> {
    try {
      const result = await this.deps.api.confirm(
        workspaceId,
        actionId,
        confirmToken,
        comment,
        signal,
      )
      const request = adaptActionRequest(result.request)
      this.deps.store.patchRequest(request)
      return { ok: true, value: request }
    } catch (cause) {
      return { ok: false, error: classifyError(cause) }
    }
  }
}
